import { useForm } from "react-hook-form";
import {
    Card,
    CardBody,
    CardHeader,
    Button,
    Chip,
    Avatar,
    Input,
} from "@heroui/react";
import { LuSave, LuCircleUserRound } from "react-icons/lu";
import { useUser } from "@/context/UserContext";
import { useFetchData, usePostData } from "@/hooks";
import { PageHeader, ThemedText, NoImageFallback } from "@/components/ui";
import { showToast } from "@/utils";

const Profile = () => {
    const { user } = useUser();

    // Fetch staff data if user is linked to staff
    const { data: staff } = useFetchData(
        user?.staff_id ? `staff/${user.staff_id}/show` : null,
    );

    const { mutate, isLoading } = usePostData("user/change-password");

    const {
        register,
        handleSubmit,
        reset,
        setError,
        formState: { errors, isValid, isDirty },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            current_password: "",
            password: "",
            password_confirmation: "",
        },
    });

    const onSubmit = (data) => {
        mutate(data, {
            method: "put",
            onSuccess: () => {
                showToast("success", "Berhasil", "Password berhasil diubah.");
                reset();
            },
            onError: (err) => {
                if (err.response?.status === 422) {
                    const serverErrors = err.response.data.errors;
                    Object.entries(serverErrors).forEach(([field, msgs]) => {
                        setError(field, { message: msgs[0] });
                    });
                }
            },
        });
    };

    return (
        <div className="flex h-full w-full flex-col gap-4">
            <PageHeader
                title="Profil Saya"
                breadcrumbs={[
                    { label: "Dashboard", href: "/" },
                    { label: "Profil" },
                ]}
            />

            <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
                {/* Info Akun */}
                <Card className="border-default-200 border" shadow="none">
                    <CardHeader className="px-4 pt-4 pb-0">
                        <ThemedText size="md" className="font-semibold">
                            Info Akun
                        </ThemedText>
                    </CardHeader>
                    <CardBody className="p-4">
                        <div className="flex items-start gap-4">
                            <Avatar
                                src={user?.avatar}
                                className="h-16 w-16 shrink-0"
                                radius="full"
                                showFallback
                                fallback={
                                    <NoImageFallback
                                        icon={LuCircleUserRound}
                                        iconSize={28}
                                    />
                                }
                            />
                            <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2">
                                <InfoItem label="Nama" value={user?.name} />
                                <InfoItem label="Email" value={user?.email} />
                                {user?.roles?.length > 0 && (
                                    <div className="flex flex-col gap-1">
                                        <ThemedText
                                            size="xs"
                                            variant="secondary"
                                            className="font-medium"
                                        >
                                            Role
                                        </ThemedText>
                                        <div className="flex flex-wrap gap-1">
                                            {user.roles.map((role) => (
                                                <Chip
                                                    key={role}
                                                    size="sm"
                                                    variant="flat"
                                                >
                                                    {role}
                                                </Chip>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </CardBody>
                </Card>

                {/* Info Kepegawaian */}
                {staff && (
                    <Card className="border-default-200 border" shadow="none">
                        <CardHeader className="px-4 pt-4 pb-0">
                            <ThemedText size="md" className="font-semibold">
                                Info Kepegawaian
                            </ThemedText>
                        </CardHeader>
                        <CardBody className="p-4">
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <InfoItem
                                    label="Jabatan"
                                    value={staff.position?.name}
                                />
                                <InfoItem
                                    label="Departemen"
                                    value={staff.department?.name}
                                />
                                <div className="flex flex-col gap-1">
                                    <ThemedText
                                        size="xs"
                                        variant="secondary"
                                        className="font-medium"
                                    >
                                        Status Kepegawaian
                                    </ThemedText>
                                    <div>
                                        <Chip
                                            color={
                                                staff.employment_status
                                                    ?.badge_color || "default"
                                            }
                                            size="sm"
                                            variant="flat"
                                            className="font-medium"
                                        >
                                            {staff.employment_status?.name ||
                                                "Unknown"}
                                        </Chip>
                                    </div>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                )}

                {/* Ubah Password */}
                <Card className="border-default-200 border" shadow="none">
                    <CardHeader className="px-4 pt-4 pb-0">
                        <ThemedText size="md" className="font-semibold">
                            Ubah Password
                        </ThemedText>
                    </CardHeader>
                    <CardBody className="p-4">
                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            className="flex flex-col gap-4"
                        >
                            <Input
                                type="password"
                                label="Password Saat Ini"
                                placeholder="Masukkan password saat ini"
                                variant="bordered"
                                isInvalid={!!errors.current_password}
                                errorMessage={errors.current_password?.message}
                                {...register("current_password", {
                                    required: "Password saat ini wajib diisi.",
                                })}
                            />
                            <Input
                                type="password"
                                label="Password Baru"
                                placeholder="Minimal 6 karakter"
                                variant="bordered"
                                isInvalid={!!errors.password}
                                errorMessage={errors.password?.message}
                                {...register("password", {
                                    required: "Password baru wajib diisi.",
                                    minLength: {
                                        value: 6,
                                        message:
                                            "Password baru minimal 6 karakter.",
                                    },
                                })}
                            />
                            <Input
                                type="password"
                                label="Konfirmasi Password Baru"
                                placeholder="Ulangi password baru"
                                variant="bordered"
                                isInvalid={!!errors.password_confirmation}
                                errorMessage={
                                    errors.password_confirmation?.message
                                }
                                {...register("password_confirmation", {
                                    required:
                                        "Konfirmasi password wajib diisi.",
                                })}
                            />

                            <Button
                                type="submit"
                                color="primary"
                                size="sm"
                                startContent={<LuSave size={16} />}
                                isDisabled={!isDirty || !isValid}
                                isLoading={isLoading}
                                className="self-start"
                            >
                                Simpan Password
                            </Button>
                        </form>
                    </CardBody>
                </Card>
            </div>
        </div>
    );
};

const InfoItem = ({ label, value }) => (
    <div className="flex flex-col gap-1">
        <ThemedText size="xs" variant="secondary" className="font-medium">
            {label}
        </ThemedText>
        <ThemedText size="sm">{value || "-"}</ThemedText>
    </div>
);

export default Profile;
